import { useEffect, useRef, useState } from "react";
import { wsUrl } from "./client";
import type { WsEvent } from "./types";

export type WsConnState = "connecting" | "open" | "retrying" | "closed";

const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15000;

// Close codes the backend might use for an auth failure now that phase 2's
// WebSocket "checks auth before accepting" (coordinator's report,
// 2026-09-20). 4401/4403 are the conventional custom-range codes for
// "unauthorized"/"forbidden" over a WS close frame; 1008 (Policy Violation)
// is the one standard code closest in meaning, so it's treated the same way
// defensively in case the backend uses it instead.
const AUTH_CLOSE_CODES = new Set([4401, 4403, 1008]);

// Deliberately NOT trying to infer an auth rejection from a fast/repeated
// close: a plain "backend is restarting or unreachable" also closes fast
// and repeatedly, and misreading that as "your session expired" would be a
// worse regression than the gap it'd close (bouncing an authenticated user
// to the login screen during a mere backend blip). The real backstop for a
// session that expired silently (no explicit close code) is AppData's own
// REST polling (/api/health, /api/stats, /api/features, each every 5-15s):
// any of those hitting a 401 fires the same global unauthorizedHandler
// within seconds regardless of what the socket saw.

/**
 * Connects to /ws/live, auto-reconnecting with exponential backoff and
 * exposing connection state for the status strip's "disconnected and
 * retrying" indicator (BUILD THIS #7 / #9).
 *
 * `authVersion` is bumped by AuthContext on login/logout so a token change
 * forces a fresh socket carrying the new (or absent) token — the effect
 * below re-runs whenever it changes, same as a path change. `onAuthError`
 * fires on a close code that signals the token was rejected/expired, so the
 * caller (AuthContext) can drop the session the same way a 401 on a normal
 * poll would, rather than just retrying a socket that will never reconnect
 * successfully with a stale token.
 */
export function useWebSocket(
  path: string,
  onEvent: (evt: WsEvent) => void,
  authVersion: number = 0,
  onAuthError?: () => void,
) {
  const [state, setState] = useState<WsConnState>("connecting");
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onAuthErrorRef = useRef(onAuthError);
  onAuthErrorRef.current = onAuthError;

  useEffect(() => {
    let closedByUs = false;
    let attempt = 0;
    let socket: WebSocket | null = null;
    let retryTimer: number | undefined;

    function connect() {
      setState((prev) => (prev === "open" ? prev : attempt === 0 ? "connecting" : "retrying"));
      try {
        socket = new WebSocket(wsUrl(path));
      } catch {
        scheduleRetry();
        return;
      }

      socket.onopen = () => {
        attempt = 0;
        setState("open");
      };

      socket.onmessage = (ev) => {
        try {
          const parsed = JSON.parse(ev.data) as WsEvent;
          onEventRef.current(parsed);
        } catch {
          // ignore malformed frame — not worth tearing down the connection for
        }
      };

      socket.onclose = (ev) => {
        if (closedByUs) return;
        if (AUTH_CLOSE_CODES.has(ev.code)) {
          onAuthErrorRef.current?.();
        }
        scheduleRetry();
      };

      socket.onerror = () => {
        socket?.close();
      };
    }

    function scheduleRetry() {
      setState("retrying");
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      retryTimer = window.setTimeout(connect, delay);
    }

    connect();

    return () => {
      closedByUs = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      socket?.close();
      setState("closed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, authVersion]);

  return state;
}
